import * as React from 'react';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent, { timelineOppositeContentClasses } from '@mui/lab/TimelineOppositeContent';
import styles from './Activities.module.scss';
import classNames from 'classnames/bind';
import { Box, Button, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { activityActions } from '~/thunks/activity/activitySlice';
import { handleGetActivity } from '~/thunks/activity/activityThunk';
import { handleApiError } from '~/utils/helper';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import InfiniteScroll from 'react-infinite-scroll-component';
import DetailsActivity from './details/DetailsActivity';
import { IActivity } from '~/utils/interface/activity';
import { useTranslation } from 'react-i18next';
import { PenIcon, PlusIcon, TrashIcon } from '~/assets/icons';

const cx = classNames.bind(styles);

type ActivityProps = {
  id: string;
};

const Activity = (props: ActivityProps) => {
  const { t } = useTranslation(['admin/job', 'admin/common']);
  const dispatch = useAppDispatch();
  const { activityList, filter } = useAppSelector((state) => state.activity);
  const [hasMore, setHasMore] = useState(true);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [selectedActivity, setSelectedActivity] = useState<IActivity | null>(null);
  useEffect(() => {
    dispatch(
      activityActions.setFilterActivity({
        page: 1,
        groupType: 'jobs',
        groupId: props.id,
      })
    );

    return () => {
      dispatch(
        activityActions.setFilterActivity({
          page: 1,
          groupType: 'jobs',
          groupId: props.id,
        })
      );
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.id]);

  useEffect(() => {
    if (filter.groupId === props.id) {
      dispatch(handleGetActivity(filter))
        .unwrap()
        .then((result) => {
          if (result.length === 0) {
            setHasMore(false);
          } else {
            setHasMore(true);
          }
        })
        .catch((e) => {
          handleApiError(e);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const groupedActivities = React.useMemo(() => {
    const grouped: Record<string, typeof activityList> = {};

    activityList.forEach((activity) => {
      const activityDate = dayjs(activity.createdAt).format('YYYY-MM-DD');
      if (!grouped[activityDate]) {
        grouped[activityDate] = [];
      }
      grouped[activityDate].push(activity);
    });

    return Object.entries(grouped).map(([date, activities]) => ({
      date,
      activities,
    }));
  }, [activityList]);

  const loadMore = () => {
    dispatch(
      activityActions.setFilterActivity({
        page: filter.page + 1,
        groupType: 'jobs',
        groupId: props.id,
      })
    );
  };

  const handleSetOpen = (open: boolean, activity?: IActivity) => {
    setOpenDialog(open);
    if (activity) {
      setSelectedActivity(activity);
    }
  };

  return (
    <InfiniteScroll
      dataLength={activityList.length}
      next={loadMore}
      hasMore={hasMore}
      height={500}
      loader={''}
      scrollableTarget='scrollableDiv'
    >
      <Timeline
        sx={{
          [`& .${timelineOppositeContentClasses.root}`]: {
            flex: 0.1,
          },
        }}
        className={cx('container')}
      >
        {groupedActivities.map(({ date, activities }) => (
          <React.Fragment key={date}>
            <TimelineItem>
              <TimelineOppositeContent color='textSecondary'>
                <Box className={cx('container-day')}>{dayjs(date).format('MMM DD, YYYY')}</Box>
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineDot />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent></TimelineContent>
            </TimelineItem>

            {activities.map((activity, index) => (
              <TimelineItem key={activity.id}>
                <TimelineOppositeContent color='textSecondary'>
                  <Box className='w-[120px]'>{dayjs(activity.createdAt).format('h:mm a')}</Box>
                </TimelineOppositeContent>
                <TimelineSeparator>
                  {activity.action === 'update' ? (
                    <TimelineDot>
                      <PenIcon className={cx('icon')} />
                    </TimelineDot>
                  ) : activity.action === 'create' ? (
                    <TimelineDot>
                      <PlusIcon className={cx('icon')} />
                    </TimelineDot>
                  ) : activity.action === 'delete' ? (
                    <TimelineDot>
                      <TrashIcon className={cx('icon')} />
                    </TimelineDot>
                  ) : (
                    <TimelineDot />
                  )}

                  {index !== activities.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>
                  <Box>{activity.note}</Box>
                  <Box className={cx('button')}>
                    <Typography className='text-[10px]'>
                      {t(`activity.by`)} {activity.createdByAccount.companyAccount.name}
                    </Typography>
                    <Button size='small' className={cx('details')} onClick={() => handleSetOpen(true, activity)}>
                      {t(`activity.details`)}
                    </Button>
                  </Box>
                </TimelineContent>
              </TimelineItem>
            ))}
          </React.Fragment>
        ))}
      </Timeline>
      <DetailsActivity open={openDialog} setOpen={handleSetOpen} data={selectedActivity ?? null} />
    </InfiniteScroll>
  );
};

export default Activity;
